import {
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  message,
  Spin,
} from "antd";
import {
  useTranslation,
} from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CheckCircle2,
  Package,
  TriangleAlert,
  Search,
} from "lucide-react";
import {
  fetchShopifySalesProductInventoryProductsDetail,
  upsertShopifySalesProductDashboardSettings,
  type ShopifySalesProductInventorySort,
} from "src/services/ShopifySalesProductAPI";
import { normalizeInventoryProductsDetailResponse } from "../utils/salesProductApiNormalize";
import { useDashboardPdfTableExpansion } from "src/hooks/useDashboardPdfTableExpansion";
import {
  getApiLangFromI18n,
} from "src/utils/apiLang";
import {
  getOptionalTenantId,
  useShopifySalesProductInventory,
  useShopifySalesProductInventoryCategories,
  useShopifySalesProductTopInventoryProducts,
} from "../hooks/useShopifySalesProductAnalytics";
import {
  DashboardNarrativePanel,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardSectionHeader,
  DashboardExportButton,
  TablePagePagination,
  dashboardDataTable as dt,
} from "src/components/common";
import {
  useDashboardNarrative,
} from "src/hooks/useDashboardNarrative";
import {
  useLanguage,
} from "src/hooks/useLanguage";
import {
  INVENTORY_STOCK_BAR_COLORS,
  PAGE_SIZE,
  NOT_AVAILABLE,
  formatInventoryTableValue,
  getStockAxisMax,
  getStockReferenceTicks,
  getTenantLowStockStorageKey,
  localizeChartDigits,
  mirrorChartMargin,
  rtlChartProps,
  readStoredLowStockThreshold,
  resolveInventoryStatusByThreshold,
  SectionTitle,
  MetricCard,
  InventoryBarValueLabel,
  InventoryStockTooltip,
  InventoryYAxisTick,
  InventoryEmptyState,
  InventoryProductImage,
  LowStockThresholdControl,
  SelectMenu,
  INVENTORY_KPI_ROW_STYLE,
} from "./salesProductShared";

const TREND_CHART_MARGIN = { top: 22, right: 56, bottom: 12, left: 0 };

export const InventorySection: FC<{
  dateRange: { startDate: string; endDate: string };
}> = ({ dateRange }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const chartRtl = rtlChartProps(isRTL);
  const lang = getApiLangFromI18n(i18n);
  const tenantId = getOptionalTenantId();
  const isAdmin = Number(localStorage.getItem("role_id")) === 1;
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [snView] = useState<"default" | "reversed">("default");
  const [inventoryStockSort, setInventoryStockSort] = useState<ShopifySalesProductInventorySort>("stock_desc");
  const [lowStockThreshold, setLowStockThreshold] = useState(() => readStoredLowStockThreshold(tenantId));
  const [lowStockInput, setLowStockInput] = useState(() => String(readStoredLowStockThreshold(tenantId)));
  const [pendingThreshold, setPendingThreshold] = useState<number | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const inventoryApiStatus =
    statusFilter === "in_stock" || statusFilter === "low_stock" || statusFilter === "out_of_stock"
      ? statusFilter
      : undefined;

  const {
    summary,
    products,
    rawData: inventoryRawData,
    totalProducts: inventoryTotalRows,
    totalPages: inventoryTotalPages,
    loading,
    error,
    detailError: inventoryDetailError,
    refetch: refetchInventory,
  } = useShopifySalesProductInventory(
    lang,
    tenantId,
    inventoryPage,
    PAGE_SIZE,
    debouncedSearch,
    inventoryApiStatus,
    categoryFilter,
    inventoryStockSort,
  );
  const {
    products: topStockProducts,
    rawData: topStockProductsRawData,
    loading: topStockProductsLoading,
  } = useShopifySalesProductTopInventoryProducts(
    lang,
    tenantId,
    debouncedSearch,
    undefined,
    categoryFilter,
  );
  const {
    categories: inventoryCategories,
    rawData: inventoryCategoriesRawData,
  } = useShopifySalesProductInventoryCategories(lang, tenantId);

  useEffect(() => {
    const stored = readStoredLowStockThreshold(tenantId);
    setLowStockThreshold(stored);
    setLowStockInput(String(stored));
  }, [tenantId]);

  useEffect(() => {
    if (summary?.lowStockThreshold === undefined) {
      return;
    }
    const threshold = Math.max(0, Math.floor(summary.lowStockThreshold));
    localStorage.setItem(getTenantLowStockStorageKey(tenantId), String(threshold));
    setLowStockThreshold(threshold);
    setLowStockInput(String(threshold));
  }, [summary?.lowStockThreshold, tenantId]);

  const { rows: exportAwareProducts, onBeforeCapture: onBeforeExportCapture, onAfterCapture: onAfterExportCapture } =
    useDashboardPdfTableExpansion(products, async (page, pageSize) => {
      const raw = await fetchShopifySalesProductInventoryProductsDetail({
        tenantId,
        lang,
        page,
        pageSize,
        search: debouncedSearch || undefined,
        status: inventoryApiStatus,
        category: categoryFilter,
        sort: inventoryStockSort,
      });
      const detail = normalizeInventoryProductsDetailResponse(raw);
      return { rows: detail.products, total: detail.total };
    });

  const productsWithThreshold = useMemo(
    () =>
      exportAwareProducts.map(product => ({
        ...product,
        status: product.status === "out_of_stock"
          ? product.status
          : resolveInventoryStatusByThreshold(product.stock, lowStockThreshold),
      })),
    [exportAwareProducts, lowStockThreshold],
  );

  const derivedInStock = useMemo(
    () => productsWithThreshold.filter(p => p.status === "in_stock").length,
    [productsWithThreshold],
  );
  const derivedLowStock = useMemo(
    () => productsWithThreshold.filter(p => p.status === "low_stock").length,
    [productsWithThreshold],
  );
  const totalProducts = summary?.totalProducts ?? inventoryTotalRows;
  const totalProductsDisplay = summary?.totalProducts !== undefined
    ? summary.totalProductsFormattedValue ?? String(totalProducts)
    : String(totalProducts);
  const inStock = summary?.inStock ?? derivedInStock;
  const inStockDisplay = summary?.inStock !== undefined
    ? summary.inStockFormattedValue ?? String(inStock)
    : String(inStock);
  const lowStock = summary?.lowStock ?? derivedLowStock;
  const lowStockDisplay = summary?.lowStock !== undefined
    ? summary.lowStockFormattedValue ?? String(lowStock)
    : String(lowStock);
  const inventoryEmptyMessage = inventoryDetailError
    ? t("salesProduct.inventory.detailsUnavailable")
    : t("salesProduct.inventory.noMatches");

  const displayedProducts = useMemo(
    () => {
      const filteredProducts = statusFilter && !inventoryApiStatus
        ? productsWithThreshold.filter(product => product.status === statusFilter)
        : productsWithThreshold;
      return snView === "reversed" ? [...filteredProducts].reverse() : filteredProducts;
    },
    [inventoryApiStatus, productsWithThreshold, snView, statusFilter],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setInventoryPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setInventoryPage(1);
  }, [inventoryStockSort]);

  function handleStatusChange(value: string) {
    setStatusFilter(value === "all" ? undefined : value);
    setInventoryPage(1);
  }

  function handleCategoryChange(value: string) {
    setCategoryFilter(value === "all" ? undefined : value);
    setInventoryPage(1);
  }

  function requestThresholdChange() {
    if (!isAdmin) {
      return;
    }
    if (!lowStockInput.trim()) {
      setLowStockInput(String(lowStockThreshold));
      return;
    }
    const next = Number(lowStockInput);
    if (!Number.isFinite(next) || next < 0) {
      setLowStockInput(String(lowStockThreshold));
      return;
    }
    const normalized = Math.floor(next);
    setLowStockInput(String(normalized));
    if (normalized < 1) {
      setPendingThreshold(null);
      message.warning(t("salesProduct.inventory.thresholdMinError"));
      return;
    }
    if (normalized !== lowStockThreshold) {
      setPendingThreshold(normalized);
    }
  }

  async function confirmThresholdChange() {
    if (pendingThreshold === null) {
      return;
    }
    setSavingThreshold(true);
    try {
      await upsertShopifySalesProductDashboardSettings({
        tenantId,
        lang,
        lowStockThreshold: pendingThreshold,
      });
      localStorage.setItem(getTenantLowStockStorageKey(tenantId), String(pendingThreshold));
      setLowStockThreshold(pendingThreshold);
      setLowStockInput(String(pendingThreshold));
      setPendingThreshold(null);
      await refetchInventory();
      message.success(t("salesProduct.inventory.thresholdUpdated"));
    } catch {
      setLowStockInput(String(lowStockThreshold));
      message.error(t("salesProduct.inventory.thresholdUpdateError"));
    } finally {
      setSavingThreshold(false);
    }
  }

  function cancelThresholdChange() {
    setPendingThreshold(null);
    setLowStockInput(String(lowStockThreshold));
  }

  const stockComparison = useMemo(
    () =>
      [...topStockProducts]
        .sort((a, b) => b.stock - a.stock)
        .slice(0, 10)
        .map(product => ({
          name: product.name.length > 18 ? `${product.name.slice(0, 18)}...` : product.name,
          fullName: product.name,
          stock: product.stock,
        })),
    [topStockProducts],
  );
  const stockAxisMax = useMemo(
    () => getStockAxisMax(stockComparison),
    [stockComparison],
  );
  const stockReferenceTicks = useMemo(
    () => getStockReferenceTicks(stockAxisMax),
    [stockAxisMax],
  );
  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t("salesProduct.inventory.allCategories") },
      ...inventoryCategories.map(category => ({ value: category, label: category })),
    ],
    [inventoryCategories, t],
  );
  const narrativePayloadData = useMemo(
    () => ({
      inventory: inventoryRawData,
      topStockProducts: topStockProductsRawData,
      categories: inventoryCategoriesRawData,
    }),
    [inventoryCategoriesRawData, inventoryRawData, topStockProductsRawData],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "sales_product_analytics",
    tabKey: "inventory",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    data: narrativePayloadData,
    enabled:
      !loading &&
      !topStockProductsLoading &&
      !error &&
      Boolean(inventoryRawData.summary || inventoryRawData.productsDetail),
  });

  return (
    <div className="sales-product-analytics-dashboard__section" ref={pdfContainerRef}>
      <DashboardSectionHeader
        title={t("salesProduct.inventory.header.title")}
        subtitle={t("salesProduct.inventory.header.subtitle")}
        sticky
        className="sales-product-analytics-dashboard__inventory-header"
        actions={(
          <>
            <LowStockThresholdControl
              value={lowStockThreshold}
              inputValue={lowStockInput}
              isAdmin={isAdmin}
              saving={savingThreshold}
              confirmOpen={pendingThreshold !== null}
              onInputChange={setLowStockInput}
              onCommitRequest={requestThresholdChange}
              onConfirm={confirmThresholdChange}
              onCancel={cancelThresholdChange}
            />
            <DashboardExportButton
              dashboardName="Inventory"
              dateRange={dateRange}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
              onBeforeCapture={onBeforeExportCapture}
              onAfterCapture={onAfterExportCapture}
            />
          </>
        )}
      />

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={narrative}
          loading={loading || topStockProductsLoading || narrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      <Spin spinning={loading} wrapperClassName="sales-product-analytics-dashboard__inventory-spin">
        <div
          className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--inventory"
          style={INVENTORY_KPI_ROW_STYLE}
          data-pdf-block="kpi-cards"
        >
          <MetricCard
            label="Total Products"
            displayLabel={t("salesProduct.inventory.kpi.totalProducts")}
            value={totalProductsDisplay}
            statusHint={t("salesProduct.inventory.kpi.totalProductsHint")}
            icon={Package}
            tooltipAlign="start"
            hideTrendSection
          />
          <MetricCard
            label="In Stock"
            displayLabel={t("salesProduct.inventory.kpi.inStock")}
            value={inStockDisplay}
            statusHint={t("salesProduct.inventory.kpi.inStockHint")}
            tone="positive"
            icon={CheckCircle2}
            tooltipAlign="start"
            hideTrendSection
          />
          <MetricCard
            label="Low Stock"
            displayLabel={t("salesProduct.inventory.kpi.lowStock")}
            value={lowStockDisplay}
            statusHint={t("salesProduct.inventory.kpi.lowStockHint")}
            tone="negative"
            icon={TriangleAlert}
            tooltipAlign="end"
            hideTrendSection
          />
         
        </div>

        <div className="sales-product-analytics-dashboard__inventory-insights-header">
          <div>
            <SectionTitle title={t("salesProduct.inventory.insightsTitle")} />
            <p className="sales-product-analytics-dashboard__card-subtitle">
              {t("salesProduct.inventory.insightsSubtitle")}
            </p>
          </div>
          <div className="sales-product-analytics-dashboard__panel-tools sales-product-analytics-dashboard__inventory-tools">
            <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
              <Search size={16} />
              <input
                type="search"
                placeholder={t("salesProduct.inventory.searchPlaceholder")}
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
              />
            </label>
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--toolbar sales-product-analytics-dashboard__select-menu--inventory"
              dropdownClassName="sales-product-analytics-dashboard__select-dropdown--inventory-categories"
              value={categoryFilter ?? "all"}
              onChange={handleCategoryChange}
              options={categoryOptions}
            />
          </div>
        </div>

        <section
          className="sales-product-analytics-dashboard__card sales-product-analytics-dashboard__inventory-chart-card"
          data-pdf-block="inventory-chart"
        >
          <SectionTitle
            title={t("salesProduct.inventory.chartTitle")}
            tooltipKey="Top 10 Product by On-hand Quantity"
            tooltipPreferSide={isRTL ? "left" : "right"}
          />
    
        
          <div className="sales-product-analytics-dashboard__chart-wrap">
            {topStockProductsLoading ? (
              <div className="sales-product-analytics-dashboard__chart-empty">
                <Spin />
              </div>
            ) : stockComparison.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stockComparison}
                    layout="vertical"
                    margin={mirrorChartMargin(TREND_CHART_MARGIN, isRTL)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" horizontal={false} vertical />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11 }}
                      domain={[0, stockAxisMax]}
                      reversed={chartRtl.axisReversed}
                      tickFormatter={value => localizeChartDigits(String(value), isRTL)}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={180}
                      orientation={chartRtl.yAxisOrientation}
                      tick={<InventoryYAxisTick isRTL={isRTL} />}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                    />
                    <Tooltip
                      content={<InventoryStockTooltip />}
                      wrapperStyle={{ pointerEvents: "none" }}
                    />
                    <Bar
                      dataKey="stock"
                      name={t("salesProduct.shared.onHandQuantity")}
                      radius={chartRtl.horizontalBarRadius}
                    >
                      {stockComparison.map((item, index) => (
                        <Cell
                          key={item.name}
                          fill={INVENTORY_STOCK_BAR_COLORS[index % INVENTORY_STOCK_BAR_COLORS.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="stock"
                        content={<InventoryBarValueLabel isRTL={isRTL} />}
                      />
                    </Bar>
                    {stockReferenceTicks.map(tick => (
                      <ReferenceLine
                        key={`stock-reference-${tick}`}
                        x={tick}
                        stroke="#ffffff"
                        strokeDasharray="3 3"
                        strokeWidth={1.4}
                      />
                    ))}
                  </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="sales-product-analytics-dashboard__chart-empty">
                <InventoryEmptyState message={inventoryEmptyMessage} />
              </div>
            )}
          </div>
        </section>

        <DashboardDataTablePanel
          as="section"
          className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--inventory"
          data-pdf-block="inventory-table"
          data-pdf-splittable
        >
          <DashboardDataTablePanelHeader>
            <SectionTitle
              title={t("salesProduct.inventory.productsTitle")}
              tooltipKey="Products"
              tooltipPreferSide={isRTL ? "left" : "right"}
            />
            <SelectMenu
              className="sales-product-analytics-dashboard__select-menu--toolbar sales-product-analytics-dashboard__select-menu--inventory-table"
              value={statusFilter ?? "all"}
              onChange={handleStatusChange}
              options={[
                { value: "all", label: t("salesProduct.inventory.statusAll") },
                { value: "in_stock", label: t("salesProduct.inventory.statusInStock") },
                { value: "low_stock", label: t("salesProduct.inventory.statusLowStock") },
                { value: "out_of_stock", label: t("salesProduct.inventory.statusOutOfStock") },
              ]}
            />
          </DashboardDataTablePanelHeader>
          <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
            <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
              <thead>
                <tr>
                  <th className="sales-product-analytics-dashboard__product-image-heading">{t("salesProduct.inventory.columnImage")}</th>
                  <th>{t("salesProduct.inventory.columnProductName")}</th>
                  <th>{t("salesProduct.inventory.columnCategory")}</th>
                  <th className={dt.number}>
                    <DashboardDataTableSortButton
                      onClick={() =>
                        setInventoryStockSort(s => (s === "stock_desc" ? "stock_asc" : "stock_desc"))
                      }
                    >
                      {t("salesProduct.inventory.columnStockCount")}
                    </DashboardDataTableSortButton>
                  </th>
                  <th>{t("salesProduct.inventory.columnStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts.length ? displayedProducts.map(product => {
                  return (
                    <tr key={product.id}>
                      <td className="sales-product-analytics-dashboard__product-image-cell">
                        <InventoryProductImage imageUrl={product.imageUrl} />
                      </td>
                      <td className={dt.cellEmphasis}>{formatInventoryTableValue(product.name)}</td>
                      <td>{formatInventoryTableValue(product.category)}</td>
                      <td className={dt.number}>
                        {product.stockUnavailable
                          ? NOT_AVAILABLE
                          : product.stockFormattedValue ?? formatInventoryTableValue(product.stock)}
                      </td>
                      <td>
                        {product.statusUnavailable ? (
                          <span className={dt.empty}>{NOT_AVAILABLE}</span>
                        ) : (
                          <span
                            className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${product.status}`}
                          >
                            {t(`salesProduct.inventory.status.${product.status}`, {
                              defaultValue: product.status.replaceAll("_", " "),
                            })}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td className="sales-product-analytics-dashboard__inventory-empty-cell" colSpan={5}>
                      <InventoryEmptyState message={inventoryEmptyMessage} />
                    </td>
                  </tr>
                )}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
          <TablePagePagination
            currentPage={inventoryPage}
            totalPages={inventoryTotalPages}
            onPrevious={() => setInventoryPage(p => Math.max(1, p - 1))}
            onNext={() => setInventoryPage(p => Math.min(inventoryTotalPages, p + 1))}
          />
        </DashboardDataTablePanel>
      </Spin>
    </div>
  );
};
